import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

/**
 * Emergent Google OAuth callback handler.
 * Detects #session_id=... in the URL fragment, posts it to the backend,
 * lets the backend set the secure httpOnly cookie, then navigates to /dashboard.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false); // synchronous flag (StrictMode-safe)
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : "";
    if (!sessionId) {
      setError("Lien de connexion Google invalide.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        setUser(data);
        // Wipe the fragment from history so refreshes don't re-trigger callback
        window.history.replaceState({}, document.title, "/dashboard");
        navigate("/dashboard", { replace: true, state: { user: data } });
      } catch (e) {
        const msg = e.response?.data?.detail || "Connexion Google échouée. Réessaie.";
        setError(typeof msg === "string" ? msg : "Connexion Google échouée. Réessaie.");
        setTimeout(() => navigate("/login", { replace: true }), 2200);
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="auth-callback">
      <div className="text-center">
        {error ? (
          <>
            <p className="text-red-600 font-semibold" data-testid="auth-callback-error">{error}</p>
            <p className="text-slate-500 text-sm mt-2">Redirection vers la page de connexion…</p>
          </>
        ) : (
          <>
            <Loader2 className="animate-spin text-indigo-500 mx-auto" size={32} />
            <p className="text-slate-600 mt-3 text-sm">Connexion sécurisée en cours…</p>
          </>
        )}
      </div>
    </div>
  );
}
